import React, { useEffect, useMemo, useState } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { toast } from 'material-react-toastify';
import { useNavigate } from 'react-router-dom';
import { sharedColors } from '../../utils/Style';
import { SliderSlide } from '../../interfaces/Slider/SliderSlide';
import { getSliderSlides } from '../../api/Slider';
import { useIsMobile } from '../../hooks/useIsMobile';

export function SliderCarousel() {
  const navigate = useNavigate();

  const isMobile = useIsMobile();

  const [hovering, setHovering] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [allSlides, setAllSlides] = useState<SliderSlide[]>([]);
  const [imagesLoaded, setImagesLoaded] = useState(false);

  const slides = useMemo(
    () =>
      allSlides.filter(
        (s) =>
          s.type === 'all' ||
          (isMobile && s.type === 'mobile') ||
          (!isMobile && s.type === 'desktop'),
      ),
    [allSlides, isMobile],
  );

  useEffect(() => {
    getSliderSlides()
      .then(setAllSlides)
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (!slides.length) return;

    const loadImage = (src: string) =>
      new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = src;
        img.onload = () => resolve();
        img.onerror = reject;
      });

    Promise.all(slides.map((s) => loadImage(s.path))).finally(() =>
      setImagesLoaded(true),
    );
  }, [slides]);

  const handleNext = () => {
    if (!slides.length) return;

    setTimeout(() => {
      setActiveIndex((prev) => (prev + 1) % slides.length);
    }, 300);
  };

  const handlePrev = () => {
    setTimeout(() => {
      setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    }, 300);
  };

  useEffect(() => {
    const t = setTimeout(handleNext, 5000);

    return () => clearTimeout(t);
  }, [activeIndex, slides]);

  return slides.length && imagesLoaded ? (
    <Box
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      sx={{
        width: '100%',
        position: 'relative',
      }}
    >
      <Box
        component='img'
        src={slides[activeIndex].path}
        sx={{
          width: '100%',
          height: 'auto',
          display: 'block',
          visibility: 'hidden',
          pointerEvents: 'none',
        }}
      />

      {slides.map((slide, i) => (
        <Box
          key={slide.id}
          component='img'
          src={slide.path}
          onClick={
            slides[activeIndex].url
              ? () => navigate(slides[activeIndex].url!)
              : undefined
          }
          sx={{
            width: '100%',
            height: 'auto',
            display: 'block',
            position: 'absolute',
            left: 0,
            top: 0,
            cursor: slides[activeIndex].url ? 'pointer' : undefined,
            opacity: activeIndex === i ? 1 : 0,
            transition: 'opacity 0.5s',
          }}
        />
      ))}

      <Box
        onClick={handlePrev}
        sx={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: sharedColors.gray7,
          opacity: hovering && slides.length > 1 ? 0.8 : 0,
          cursor: 'pointer',
        }}
      >
        <IconButton sx={{ color: sharedColors.white }}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>

      <Box
        onClick={handleNext}
        sx={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          backgroundColor: sharedColors.gray7,
          opacity: hovering && slides.length > 1 ? 0.8 : 0,
          cursor: 'pointer',
        }}
      >
        <IconButton sx={{ color: sharedColors.white }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  ) : (
    <></>
  );
}
